import { ValidationError } from '@tanstack/react-form'
import { ReactNode, useState } from 'react'
import { validationErrorMessages } from './validation-error-message'

interface AmountFieldProps {
	value: number
	onChange: (value: number) => void
	label: ReactNode
	hint?: ReactNode
	locale: string
	prefix?: ReactNode
	suffix?: ReactNode
	placeholder?: string
	integer?: boolean
	minimumFractionDigits?: number
	maximumFractionDigits?: number
	errors?: Array<ValidationError>
	readOnly?: boolean
	onFocus?: () => void
	onBlur?: () => void
	hideLabel?: boolean
}

export function AmountField({
	value,
	onChange,
	label,
	hint,
	locale,
	prefix,
	suffix,
	placeholder,
	integer = false,
	minimumFractionDigits,
	maximumFractionDigits,
	errors,
	readOnly = false,
	onFocus,
	onBlur,
	hideLabel = false,
}: AmountFieldProps) {
	const formatter = new Intl.NumberFormat(locale, {
		minimumFractionDigits: minimumFractionDigits ?? (integer ? 0 : 2),
		maximumFractionDigits: maximumFractionDigits ?? (integer ? 0 : 2),
		useGrouping: false,
	})
	const [isFocused, setIsFocused] = useState(false)
	const [draft, setDraft] = useState<string>(() => (value > 0 ? value.toString() : ''))

	const display = isFocused
		? draft
		: value > 0
		? formatter.format(value)
		: ''

	const handleChange = (text: string) => {
		setDraft(text)
		const allowed = integer ? text.replace(/[^\d-]/g, '') : text.replace(/[^\d.,-]/g, '').replace(/,/g, '.')
		if (allowed === '' || allowed === '.' || allowed === '-') {
			onChange(0)
			return
		}
		const parsed = integer ? parseInt(allowed, 10) : parseFloat(allowed)
		if (!isNaN(parsed)) { onChange(parsed) }
	}

	const errorMessages = validationErrorMessages(errors)
	const hasErrors = errorMessages.length > 0

	return (
		<label className="flex flex-col gap-2">
			{!hideLabel && <span className="text-base text-grey-900">{label}</span>}
			<div
				className={`flex items-center rounded-md ring-1 bg-white focus-within:ring-4 transition-shadow ${
					hasErrors
						? 'ring-red-300 focus-within:ring-red-100'
						: 'ring-grey-100 focus-within:ring-blue-100'
				} ${readOnly ? 'opacity-60' : ''}`}
			>
				{prefix && (
					<span className="pl-3 pr-2 text-grey-500 select-none text-base" aria-hidden="true">
						{prefix}
					</span>
				)}
				<input
					type="text"
					inputMode={integer ? 'numeric' : 'decimal'}
					value={display}
					onChange={readOnly ? undefined : (e) => handleChange(e.target.value)}
					onFocus={() => {
						setIsFocused(true)
						setDraft(value > 0 ? value.toString() : '')
						onFocus?.()
					}}
					onBlur={() => {
						setIsFocused(false)
						onBlur?.()
					}}
					placeholder={placeholder}
					readOnly={readOnly}
					disabled={readOnly}
					className={`flex-1 py-2 outline-none bg-transparent text-grey-800 tabular-nums text-base min-w-0 ${
						prefix ? 'pr-2' : 'px-3'
					} ${readOnly ? 'cursor-not-allowed' : ''}`}
				/>
				{suffix && (
					<span
						className="px-3 text-xs font-medium text-grey-500 select-none whitespace-nowrap"
						aria-hidden="true"
					>
						{suffix}
					</span>
				)}
			</div>
			{errorMessages.map((message, index) => (
				<div className="text-red-500 text-sm" key={index}>
					{message}
				</div>
			))}
			{hint && <div className="text-sm text-grey-700">{hint}</div>}
		</label>
	)
}
